import * as React from 'react';
import { styled, useTheme, Theme, CSSObject, alpha, darken } from '@mui/material/styles';
import Box from '@mui/material/Box';
import MuiDrawer from '@mui/material/Drawer';
import MuiAppBar, { AppBarProps as MuiAppBarProps } from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import List from '@mui/material/List';
import CssBaseline from '@mui/material/CssBaseline';
import Typography from '@mui/material/Typography';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import MenuIcon from '@mui/icons-material/Menu';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import { appName } from '../../../configs';
import { getImageUrl } from '../../../helpers';
import lightTheme from '../../../theme/light';
import { AccountBalanceOutlined, AccountBalanceWalletOutlined, AdminPanelSettingsOutlined, ApartmentOutlined, BookmarkRemoveOutlined, CardGiftcardOutlined, ContactPhoneOutlined, EngineeringOutlined, LocationCity, EventNoteOutlined, ExpandLess, ExpandMore, FactCheckOutlined, GroupsOutlined, HotelOutlined, InsightsOutlined, LocalOfferOutlined, LockOutlined, LocationCityOutlined, MailOutline, ManageAccountsOutlined, MeetingRoomOutlined, PaymentsOutlined, PendingActionsOutlined, PeopleOutline, PowerOutlined, QueryStatsOutlined, RequestQuoteOutlined, StarRateOutlined, SupervisorAccountOutlined, SwapHorizOutlined, ViewQuiltOutlined } from '@mui/icons-material'
import { Connect, mapDispatchToProps, mapStateToProps } from '../../../Redux';
import { Avatar, Button, ButtonBase, Collapse, Dialog, Link, Tooltip } from '@mui/material';
import { useLocation, useNavigate } from 'react-router';
import { PropertyProps } from '../../../types/properties';
import ProfileMenu from '../profileMenu';
import logo from '../../../assets/images/logo.png'
import whiteLogo from '../../../assets/images/whiteLogo.png'
import AlertDialogSlide from '../alert';
import { UserProps } from '../../../types/user';
import useAccessRole from '../useAccessRole';
export const terms_urls = ['/terms_conditions', '/privacy_policies', '/faqs', '/contact_us']
const { pathname } = window.location

const drawerWidth = terms_urls.includes(pathname) ? 0 : 240;

const openedMixin = (theme: Theme): CSSObject => ({
  width: drawerWidth,
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  overflowX: 'hidden',
});

const closedMixin = (theme: Theme): CSSObject => ({
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  overflowX: 'hidden',
  width: `calc(${theme.spacing(7)} + 1px)`,
  [theme.breakpoints.up('sm')]: {
    width: `calc(${theme.spacing(8)} + 1px)`,
  },
});

const DrawerHeader = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: theme.spacing(0, 1),
  // necessary for content to be below app bar
  ...theme.mixins.toolbar,
}));

interface AppBarProps extends MuiAppBarProps {
  open?: boolean;
}


const AppBar = styled(MuiAppBar, {
  shouldForwardProp: (prop) => prop !== 'open',
})<AppBarProps>(({ theme, open }) => ({
  zIndex: theme.zIndex.drawer + 1,
  transition: theme.transitions.create(['width', 'margin'], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  ...(open && {
    marginLeft: drawerWidth,
    width: `calc(100% - ${drawerWidth}px)`,
    transition: theme.transitions.create(['width', 'margin'], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
  }),
}));

const Drawer = styled(MuiDrawer, { shouldForwardProp: (prop) => prop !== 'open' })(
  ({ theme, open }) => ({
    width: drawerWidth,
    flexShrink: 0,
    whiteSpace: 'nowrap',
    boxSizing: 'border-box',
    ...(open && {
      ...openedMixin(theme),
      '& .MuiDrawer-paper': openedMixin(theme),
    }),
    ...(!open && {
      ...closedMixin(theme),
      '& .MuiDrawer-paper': closedMixin(theme),
    }),
  }),
);
interface Props {
  selectedProperty?: PropertyProps
  properties?: PropertyProps[]
  drawerOpen?: boolean
  User?: UserProps
  setRedux?: (data: any) => void
  logOut?: () => void
}
interface StateProps {
  showAlert: boolean
  paymentLocked: boolean
}

const MiniDrawer: React.FC<Props> = ({
  selectedProperty,
  properties,
  drawerOpen,
  User,
  setRedux,
  logOut
}) => {
  const theme = useTheme();
  const navigate = useNavigate()
  const accessRole = useAccessRole()
  const open = drawerOpen
  const [state, setState] = React.useState<StateProps>({
    showAlert: false,
    paymentLocked: false
  })
  const { showAlert, paymentLocked } = state
  const paymentAccountRequiredPages = ['/units_labels', '/tenants']
  const adminPages = ['/admin_general', '/all_properties', '/bank_dashboard', '/admin_users', '/admin_withdrawals', '/admin_landlords', '/admin_referrals', '/admin_payments', '/admin_smart_locks', '/admin_locks', '/admin_lock_orders', '/admin_unlock_records', '/admin_bnb', '/admin_bnb_reported_issues']
  const [adminOpen, setAdminOpen] = React.useState(adminPages.includes(pathname))

  const listOptions = [
    { label: 'Dashboard', Icon: <ViewQuiltOutlined />, page: '/dashboard' },
    // { label: 'Messages', Icon: <MailOutline />, page: '/messages' },
    { label: 'Units', Icon: <ApartmentOutlined />, page: '/units', subUrl: ['/new_unit'] },
    { label: 'Units Labels', Icon: <LocalOfferOutlined />, page: '/units_labels', subUrl: ['/new_unitLabel'] },
    { label: 'Utilities & Amenities', Icon: <PowerOutlined />, page: '/utilities', subUrl: ['/new_utility'] },
    { label: 'Tenants', Icon: <PeopleOutline />, page: '/tenants', subUrl: ['/new_tenant', '/tenant', '/new_email'] },
    // { label: 'BnBs', Icon: <HotelOutlined />, page: '/bnb' },
    { label: 'Collaborators', Icon: <SupervisorAccountOutlined />, page: '/collaborators', subUrl: ['/add_collaborator'] },
    { label: 'Maintenances', Icon: <EngineeringOutlined />, page: '/maintenances', subUrl: ['/new_maintenance'] },
    { label: 'Vacation Notices', Icon: <MeetingRoomOutlined />, page: '/vacation_notices', subUrl: ['/vacation_notices/process'] },
    { label: 'Reminder Dates', Icon: <EventNoteOutlined />, page: '/reminder_dates', subUrl: [] },
    { label: 'Contacts', Icon: <ContactPhoneOutlined />, page: '/contacts', subUrl: ['/new_contact'] },
    { label: 'Reviews', Icon: <StarRateOutlined />, page: '/reviews', subUrl: [] }
  ]
  const listOptions0 = [
    { label: 'Payment Account', Icon: <AccountBalanceWalletOutlined />, page: '/payment_account', subUrl: [] },
    { label: 'Deductions', Icon: <BookmarkRemoveOutlined />, page: '/deductions', subUrl: [] },
    { label: 'Arrears', Icon: <PendingActionsOutlined />, page: '/arrears', subUrl: ['/new_arrear'] },
    { label: 'Summary', Icon: <RequestQuoteOutlined />, page: '/invoices', subUrl: ['/new_invoice'] },
    { label: 'Payments', Icon: <PaymentsOutlined />, page: '/payments', subUrl: ['/new_payment'] },
    { label: 'Expenditures', Icon: <FactCheckOutlined />, page: '/expenditures' },
  ]
  const adminOptions = [
    ...(User?._id === '66c8ca3fd5a4fe27825dcad4' ? [
      {
        label: 'Admin', Icon: <AdminPanelSettingsOutlined />, children: [
          { label: 'General', Icon: <InsightsOutlined />, page: '/admin_general' },
          { label: 'All Properties', Icon: <LocationCity />, page: '/all_properties' },
          { label: 'Users', Icon: <ManageAccountsOutlined />, page: '/admin_users' },
          { label: 'Landlords', Icon: <GroupsOutlined />, page: '/admin_landlords' },
          { label: 'Withdrawals', Icon: <SwapHorizOutlined />, page: '/admin_withdrawals' },
          { label: 'Payments', Icon: <PaymentsOutlined />, page: '/admin_payments' },
          { label: 'Referrals', Icon: <CardGiftcardOutlined />, page: '/admin_referrals' },
          // smart locks, catalogue, orders, installers and unlock records are tabs of this one page
          { label: 'Locks', Icon: <LockOutlined />, page: '/admin_locks' },
          // reported issues, for now - a bookings overview can join it as another tab later
          { label: 'BnB', Icon: <HotelOutlined />, page: '/admin_bnb' },
          { label: 'Bank', Icon: <AccountBalanceOutlined />, page: '/bank_dashboard' },
        ]
      },
    ] : []),
    // an admin (granted by the super admin under Users) only gets the Locks page (without its super-admin-only tabs)
    ...(User?._id !== '66c8ca3fd5a4fe27825dcad4' && accessRole === 'admin' ? [
      { label: 'Admin', Icon: <AdminPanelSettingsOutlined />, children: [{ label: 'Locks', Icon: <LockOutlined />, page: '/admin_locks' }] },
    ] : []),
  ]
  const rightItems = [
    { label: 'Properties', page: '/properties', Icon: <LocationCityOutlined sx={{ color: '#fff' }} /> },
    { label: 'Statistics', page: '/statistics', Icon: <QueryStatsOutlined sx={{ color: '#fff' }} /> },
    {
      label: 'My Account', Icon: <ProfileMenu
        User={User}
        onLogout={() => {
          if (logOut) {
            logOut()
            window.location.href = '/'
          }
        }}
      />
    },
  ]

  const location = useLocation()

  const handleDrawerAction = () => {
    if (setRedux)
      setRedux({
        drawerOpen: !drawerOpen
      })
  };
  const showTermsOrPolicies = terms_urls.includes(pathname)

  return (
    User || showTermsOrPolicies ?
      <Box sx={{
        display: 'flex'
      }}>
        <CssBaseline />
        <AppBar position="fixed" open={open}>
          <Toolbar sx={{
            display: 'flex',
            justifyContent: 'space-between'
          }}>
            <Box sx={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center'
            }}>
              <IconButton
                color="inherit"
                aria-label="open drawer"
                onClick={handleDrawerAction}
                edge="start"
                sx={{
                  mr: 5,
                  ...(open && { display: 'none' }),
                }}
              >
                <MenuIcon />
              </IconButton>
              {
                !open || showTermsOrPolicies ?
                  <Link sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    alignItems: 'center'
                  }}
                    href={'/'}
                  >
                    <img
                      alt='logo'
                      src={whiteLogo}
                      style={{ height: 50, width: 50, objectFit: 'contain' }} />
                    <Typography
                      color={'#fff'}
                      sx={{ fontSize: 20, ml: 1 }}
                    >{appName}</Typography>
                  </Link> :
                  <React.Fragment />
              }
            </Box>
            {
              !showTermsOrPolicies ?

                <Box sx={{
                  display: 'flex',
                  flexDirection: 'row'
                }}>
                  {
                    rightItems.map(({ Icon, label, page }) => (

                      label === 'My Account' ?
                        <React.Fragment key={label}>
                          {Icon}
                        </React.Fragment>
                        :
                        <Tooltip key={label} title={label}>
                          <IconButton onClick={() => navigate(page as string)}>
                            {Icon}
                          </IconButton>
                        </Tooltip>
                    ))
                  }
                </Box> :
                <React.Fragment />
            }
          </Toolbar>
        </AppBar>
        {
          showTermsOrPolicies ?
            <React.Fragment /> :
            <Drawer
              variant="permanent"
              open={open}
            >
              <DrawerHeader sx={{ pl: 1 }}>
                <img
                  src={logo}
                  style={{
                    height: 50,
                    width: 50,
                    objectFit: 'contain'
                  }}
                  alt='logo' />
                <Typography
                  color={lightTheme.palette.primary.main}
                  sx={{ fontSize: 20 }}
                >{appName}</Typography>
                <IconButton onClick={handleDrawerAction}>
                  {theme.direction === 'rtl' ? <ChevronRightIcon /> : <ChevronLeftIcon />}
                </IconButton>
              </DrawerHeader>
              <Divider sx={{ ml: 2, mr: 2 }} />
              <Tooltip title={open ? '' : (selectedProperty?.name || 'Select Property')} placement='right'>
                <ButtonBase
                  onClick={() => navigate('/properties')}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: open ? 'flex-start' : 'center',
                    width: open ? 'auto' : 40,
                    mx: open ? 1.5 : 'auto',
                    mt: 1.5,
                    mb: 1.5,
                    p: 1,
                    borderRadius: 2,
                    transition: 'box-shadow 0.2s, transform 0.2s',
                    ...(selectedProperty ? {
                      background: `linear-gradient(135deg, ${lightTheme.palette.primary.main} 0%, ${darken(lightTheme.palette.primary.main, 0.45)} 100%)`,
                      boxShadow: `0 3px 10px ${alpha(lightTheme.palette.primary.main, 0.4)}`,
                      '&:hover': {
                        boxShadow: `0 4px 14px ${alpha(lightTheme.palette.primary.main, 0.55)}`,
                        transform: 'translateY(-1px)',
                      }
                    } : {
                      border: '1px dashed',
                      borderColor: lightTheme.palette.primary.main,
                      bgcolor: alpha(lightTheme.palette.primary.main, 0.06),
                      '&:hover': {
                        bgcolor: lightTheme.palette.primary.contrastText,
                      }
                    })
                  }}
                >
                  <Avatar
                    src={getImageUrl(selectedProperty?.logos, 'small')}
                    sx={{
                      height: 32,
                      width: 32,
                      bgcolor: selectedProperty ? alpha('#fff', 0.2) : lightTheme.palette.primary.main,
                      border: selectedProperty ? '2px solid rgba(255,255,255,0.55)' : 'none',
                    }}
                  >
                    {!selectedProperty ? <ApartmentOutlined sx={{ fontSize: 17, color: '#fff' }} /> : null}
                  </Avatar>
                  {!open ? null :
                    <Box sx={{
                      ml: 1.5,
                      overflow: 'hidden',
                      textAlign: 'left'
                    }}>
                      <Typography sx={{
                        fontSize: 10,
                        fontWeight: 600,
                        letterSpacing: 0.5,
                        color: selectedProperty ? 'rgba(255,255,255,0.75)' : 'text.secondary',
                        textTransform: 'uppercase',
                        lineHeight: 1.4
                      }}>
                        Property
                      </Typography>
                      <Box sx={{
                        width: 130,
                        fontSize: 13,
                        fontWeight: selectedProperty ? 600 : 500,
                        color: selectedProperty ? '#fff' : lightTheme.palette.primary.main,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        lineHeight: 1.4
                      }}>
                        {selectedProperty?.name || 'Select a property'}
                      </Box>
                    </Box>
                  }
                  {open ? <ExpandMore sx={{ fontSize: 18, color: selectedProperty ? 'rgba(255,255,255,0.85)' : 'text.secondary', ml: 1 }} /> : null}
                </ButtonBase>
              </Tooltip>
              <Divider sx={{ ml: 2, mr: 2 }} />
              {
                [listOptions, listOptions0, adminOptions].map((options, i) => (
                  <Box key={i} sx={{ mt: i === 2 ? 'auto' : 0 }}>
                    {
                      (i === 1 || i === 2) && options.length > 0 ?
                        <Divider sx={{ ml: 2, mr: 2 }} /> :
                        null
                    }
                    <List dense disablePadding>
                      {options.map((option: any) => {
                        const { label, Icon, page, subUrl, children } = option
                        const handleClick = (clickPage: string) => {
                          const noPropertyNeeded = [...adminPages]
                          if (paymentAccountRequiredPages.includes(clickPage) && selectedProperty && !selectedProperty.hasPaymentAccount) {
                            setState({ ...state, paymentLocked: true })
                            return
                          }
                          if (adminPages.includes(clickPage)) {
                            navigate(clickPage)
                          } else {
                            let Page = clickPage
                            if (!selectedProperty && !noPropertyNeeded.includes(clickPage))
                              Page = '/properties'
                            navigate(Page)
                            if (!selectedProperty && !noPropertyNeeded.includes(Page))
                              setState({ ...state, showAlert: true })
                          }
                        }
                        if (children) {
                          const isChildActive = children.some((child: any) => child.page === location.pathname)
                          return (
                            <React.Fragment key={label}>
                              <ListItem
                                disablePadding
                                sx={{
                                  display: 'block',
                                  borderRadius: 1,
                                  mx: 0.5,
                                }}>
                                <Tooltip title={open ? '' : label}>
                                  <ListItemButton
                                    sx={{
                                      minHeight: 36,
                                      justifyContent: open ? 'initial' : 'center',
                                      px: 1.5,
                                      py: 0.5,
                                      borderRadius: 1,
                                    }}
                                    onClick={() => open ? setAdminOpen(!adminOpen) : handleClick(children[0].page)}
                                  >
                                    <ListItemIcon
                                      sx={{
                                        minWidth: 0,
                                        mr: open ? 1.5 : 'auto',
                                        justifyContent: 'center',
                                        '& .MuiSvgIcon-root': { fontSize: 18 },
                                      }}
                                    >
                                      {Icon}
                                    </ListItemIcon>
                                    <ListItemText
                                      primary={label}
                                      sx={{ opacity: open ? 1 : 0 }}
                                      primaryTypographyProps={{ fontSize: 13 }}
                                    />
                                    {open ? (adminOpen ? <ExpandLess sx={{ fontSize: 18 }} /> : <ExpandMore sx={{ fontSize: 18 }} />) : null}
                                  </ListItemButton>
                                </Tooltip>
                              </ListItem>
                              <Collapse in={open && (adminOpen || isChildActive)} timeout="auto" unmountOnExit>
                                <List dense disablePadding>
                                  {children.map((child: any) => (
                                    <ListItem
                                      key={child.label}
                                      disablePadding
                                      sx={{
                                        display: 'block',
                                        bgcolor: child.page === location.pathname ? theme.palette.primary.contrastText : 'transparent',
                                        borderRadius: 1,
                                        mx: 0.5,
                                        pl: 2,
                                      }}>
                                      <Tooltip title={''}>
                                        <ListItemButton
                                          sx={{
                                            minHeight: 36,
                                            justifyContent: 'initial',
                                            px: 1.5,
                                            py: 0.5,
                                            borderRadius: 1,
                                          }}
                                          onClick={() => handleClick(child.page)}
                                        >
                                          <ListItemIcon
                                            sx={{
                                              minWidth: 0,
                                              mr: 1.5,
                                              justifyContent: 'center',
                                              '& .MuiSvgIcon-root': { fontSize: 18 },
                                            }}
                                          >
                                            {child.Icon}
                                          </ListItemIcon>
                                          <ListItemText
                                            primary={child.label}
                                            sx={{ opacity: 1 }}
                                            primaryTypographyProps={{ fontSize: 13 }}
                                          />
                                        </ListItemButton>
                                      </Tooltip>
                                    </ListItem>
                                  ))}
                                </List>
                              </Collapse>
                            </React.Fragment>
                          )
                        }
                        return (
                          <ListItem
                            key={label}
                            disablePadding
                            sx={{
                              display: 'block',
                              bgcolor: (
                                page === location.pathname ||
                                subUrl?.includes(location.pathname)
                              ) ? theme.palette.primary.contrastText : 'transparent',
                              borderRadius: 1,
                              mx: 0.5,
                            }}>
                            <Tooltip title={open ? '' : label}>
                              <ListItemButton
                                sx={{
                                  minHeight: 36,
                                  justifyContent: open ? 'initial' : 'center',
                                  px: 1.5,
                                  py: 0.5,
                                  borderRadius: 1,
                                }}
                                onClick={() => handleClick(page)}
                              >
                                <ListItemIcon
                                  sx={{
                                    minWidth: 0,
                                    mr: open ? 1.5 : 'auto',
                                    justifyContent: 'center',
                                    '& .MuiSvgIcon-root': { fontSize: 18 },
                                  }}
                                >
                                  {Icon}
                                </ListItemIcon>
                                <ListItemText
                                  primary={label}
                                  sx={{ opacity: open ? 1 : 0 }}
                                  primaryTypographyProps={{ fontSize: 13 }}
                                />
                              </ListItemButton>
                            </Tooltip>
                          </ListItem>
                        )
                      })}
                    </List>
                  </Box>
                ))
              }
            </Drawer>
        }
        {
          showAlert ?
            <AlertDialogSlide
              title='Select Property'
              message='Please set a property to default before using this feature.'
              close={() => setState({ ...state, showAlert: false })}
            /> :
            <React.Fragment />
        }
        {
          paymentLocked ?
            <Dialog
              open
              onClose={() => setState({ ...state, paymentLocked: false })}
              PaperProps={{ sx: { borderRadius: 3 } }}
            >
              <Box sx={{ p: 3, maxWidth: 340 }}>
                <Typography sx={{ fontWeight: 700, fontSize: 16, mb: 1 }}>
                  Payment Account Required
                </Typography>
                <Typography sx={{ fontSize: 13, color: 'text.secondary', mb: 3 }}>
                  Set up a payment account for this property before you can use this feature.
                </Typography>
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                  <Button
                    onClick={() => setState({ ...state, paymentLocked: false })}
                    color="inherit"
                    sx={{ textTransform: 'none' }}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="contained"
                    sx={{ textTransform: 'none' }}
                    onClick={() => {
                      setState({ ...state, paymentLocked: false })
                      navigate('/payment_account')
                    }}
                  >
                    Set Up Now
                  </Button>
                </Box>
              </Box>
            </Dialog> :
            <React.Fragment />
        }
      </Box> :
      <React.Fragment />
  );
}
export default Connect(mapStateToProps, mapDispatchToProps)(MiniDrawer)